import { useTranslation } from "next-i18next";

import { IconNavigationCross } from "@/assets/icons/figma/IconNavigationCross";
import { cn } from "@/common/utils/css.utils";
import { useSearchContext } from "@/features/search/context/SearchContext";
import {
  useFacetLabel,
  useFacetValueLabel,
} from "@/features/search/hooks/useFacetLabel";
import { getAppliedFilters } from "@/features/search/utils/applied-filters.utils";
import { getVisibleFacets } from "@/features/search/utils/facets.utils";

/**
 * Removable chips for every applied filter, derived from the response facets.
 *
 * Filters the landing page itself applies are left out — the service marks them `locked`, and the
 * URL can't clear them, so a chip with a remove button would be a lie. What defines the page is
 * described by the breadcrumb instead.
 */
export const ActiveFilters = () => {
  const { t } = useTranslation();
  const { query, navigation } = useSearchContext();
  const facetLabel = useFacetLabel();
  const facetValueLabel = useFacetValueLabel();
  const facets = getVisibleFacets(query.data?.facets ?? []);

  /* A choice is a range underneath — the page can own it (/sale sets a discount floor), and it is
   * cleared by dropping its param rather than by toggling a value off. */
  const chips = getAppliedFilters(facets, facetLabel, facetValueLabel, {
    from: t("common:common-from"),
    to: t("common:common-to"),
  })
    .filter(filter => !filter.locked)
    .map(filter => ({
      ...filter,
      onRemove: () => {
        if (filter.kind === "terms" && filter.value != null) {
          return navigation.toggleTerm(filter.facetKey, filter.value);
        }

        return filter.kind === "choice" && filter.param
          ? navigation.setParam(filter.param, null)
          : navigation.setRange(filter.facetKey, null, null);
      },
    }));

  if (!chips.length) {
    return null;
  }

  return (
    <fieldset
      disabled={query.isPlaceholderData}
      className={cn(
        /* Wrapping rows need the negative-margin form: `space-x` alone leaves the first chip of
           every wrapped line indented and the lines themselves touching. */
        "-ml-2 -mt-2 flex min-w-0 flex-wrap items-center space-x-2 space-y-2 border-0 p-0 transition-opacity",
        "[&>:first-child]:ml-2 [&>:first-child]:mt-2",
        query.isPlaceholderData && "opacity-60"
      )}
    >
      {chips.map(chip => (
        <button
          key={chip.key}
          type="button"
          onClick={chip.onRemove}
          className="flex items-center space-x-1 rounded-full bg-grey-100 py-1 pl-3 pr-2 font-body text-sm text-black hover:bg-grey-200"
        >
          {/* Wide enough for "<facet>: <value>" — a free-shipping chip carries both, and
              "Free shipping: Netherl…" names neither half. Still capped, still one line. */}
          <span className="max-w-64 truncate">{chip.label}</span>
          <IconNavigationCross className="h-4 w-4 text-grey-500" />
        </button>
      ))}
      <button
        type="button"
        onClick={() => navigation.reset()}
        className="font-body text-sm font-semibold text-brand-500 hover:underline"
      >
        {t("listing:filter-clear-all")}
      </button>
    </fieldset>
  );
};
